import { ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import { ToolbarTheme } from '@fluentui/react-teams/lib/esm/components/Toolbar/ToolbarTheme';
import React from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { appState } from '../AppState';
import { BrowserAppTemplate } from '../browser-pages/BrowserAppTemplate';
import { canOpenSettingsPage } from '../settings/settingsAccess';
import { AccessDenied } from '../shared/components/AccessDenied';
import { SkeletonBoardHeader } from '../shared/components/Skeleton';
import { ConsentState, SignedInConsentState } from '../shared/services/signInState.service';
import { ADMINISTRATORS, AGENTS, ANALYSTS, KNOWLEDGEAGENT, KNOWLEDGEREADER } from '../shared/utils/constants';

export const SkeletonPage = ({ children }) => {
  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <>
          <ToolbarTheme globalTheme={globalTheme}>
            <SkeletonBoardHeader />
          </ToolbarTheme>
          {children}
        </>
      )} />
  )
}

export const ChangeSkeletonVersion = ({ children }) => {
  return (
    <BrowserAppTemplate page={
      <div style={{ height: "calc(100vh - 50px)" }}>
        {children}
      </div>}
      active={'ticket'}
    />
  );
}

export const AuthorizedAdminOrAnalystsRoute = ({
  component: Component,
  ...rest
}) => {
  const { roles, isLoading } = appState().userRoles;
  const [signedIn] = SignedInConsentState.useIsSignedIn();

  return (
    <Route {...rest} render={props =>
      (signedIn !== ConsentState.SignedIn ||
        // Once the auth provider reports SignedIn, this can re-render before meAuthentication()
        // has finished populating userRoles.roles - failing open on signedIn alone isn't enough,
        // or a still-empty roles array reads as "no access" and redirects off the ticket page.
        isLoading ||
        roles.includes(ADMINISTRATORS) ||
        roles.includes(ANALYSTS) || 
        roles.includes(AGENTS) || 
        roles.includes(KNOWLEDGEAGENT) ||
        roles.includes(KNOWLEDGEREADER)) ? 
        <Component {...props} /> : 
           rest.path.includes('/tickets/:ticketId') ? 
          <Redirect to={{ pathname: `/requests/${props.match.params.ticketId}`, state: { from: props.location } }} /> : 
          <Redirect to={{ pathname: '/requests', state: { from: props.location } }} />
    } />
  );
};

/**
 * A settings page, open to each role as settingsAccess decides (#31467); anyone else gets the no-access page. Like
 * AuthorizedAdminOrAnalystsRoute it renders while sign-in or the roles are still loading, so a page does not flash
 * no-access on every load; the API behind it is scoped server-side.
 */
interface SettingsRouteParams {
  settingsKey?: string;
  securityKey?: string;
}

type AuthorizedSettingsRouteProps = Omit<RouteProps, 'component' | 'render'> & {
  component: React.ComponentType<RouteComponentProps<SettingsRouteParams>>;
};

export const AuthorizedSettingsRoute = ({
  component: Component,
  ...rest
}: AuthorizedSettingsRouteProps) => {
  const { roles, isLoading } = appState().userRoles;
  const [signedIn] = SignedInConsentState.useIsSignedIn();

  return (
    <Route {...rest} render={(props: RouteComponentProps<SettingsRouteParams>) => {
      const { settingsKey, securityKey } = props.match.params;
      const page = [settingsKey, securityKey].filter(Boolean).join('/');
      return (signedIn !== ConsentState.SignedIn || isLoading || canOpenSettingsPage(roles, page)) ?
        <Component {...props} /> :
        <BrowserAppTemplate page={<AccessDenied />} active={settingsKey} />;
    }} />
  );
};
